import apiClient from './apiClient';
import { Order } from '../types/cart.types';
import { Transaction } from '../types/transaction.types';
import { request } from 'http';

export interface ManualPaymentRequest {
  agencyId: number;
  amount: number;
  paymentMethod: 'CARD'|'BANK_TRANSFER' | 'CHECK' | 'CASH' | 'OTHER';
  reference?: string;
  paymentDate: string;
  description?: string;
  attachmentFile?: File;
}

export interface ManualPaymentResponse {
  transactionId: number;
  status: 'REQUESTED'|'PENDING' | 'APPROVED' | 'REJECTED' | 'COMPLETED_AND_CONSUMED';
  type:  'ADD_BALANCE' | 'BANK_TRANSFER' | 'CHECK' | 'CASH' | 'OTHER';
  message: string;
}

export interface ManualPaymentFilter {
  agencyId?: number;
  status?: 'REQUESTED'|'PENDING' | 'APPROVED' | 'REJECTED' |  'COMPLETED_AND_CONSUMED' | 'CANCELED';
  type?:  'ADD_BALANCE' | 'BANK_TRANSFER' | 'CHECK' | 'CASH' | 'OTHER';
  startDate?: string;
  endDate?: string;
  //page?: number;
  //pageSize?: number;
}

/**
 * Service pour gérer les paiements manuels (virements bancaires, chèques, etc.)
 */
export const manualPaymentService = {

  async requestBalanceTopUp(amount: number, description: string): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post('v1/balance/request?amount=' + amount + '&description=' + encodeURIComponent(description), {
      amount,
      description
    });
    return response.data;
  },
  async balanceStripeTopUp(paymentIntentId: string, id: number, externalReference:string): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post('v1/balance/card', {
      
        paymentIntentId,
        id,
        externalReference
      
    });
    return response.data;
  },

  /**
   * Soumet une demande de paiement manuel
   * @param paymentData Données du paiement manuel
   */
  async submitPayment(paymentData: ManualPaymentRequest): Promise<ManualPaymentResponse> {
    // Création d'un FormData pour pouvoir envoyer le fichier
    const formData = new FormData();
    
    // Ajout des données du paiement
    formData.append('agencyId', paymentData.agencyId.toString());
    formData.append('amount', paymentData.amount.toString());
    formData.append('paymentMethod', paymentData.paymentMethod);
    formData.append('paymentDate', paymentData.paymentDate);
    
    if (paymentData.reference) {
      formData.append('reference', paymentData.reference);
    }
    
    if (paymentData.description) {
      formData.append('description', paymentData.description);
    }
    
    if (paymentData.attachmentFile) {
      formData.append('attachmentFile', paymentData.attachmentFile);
    }
    
    const response = await apiClient.post<ManualPaymentResponse>(
      '/payments/manual', 
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    
    return response.data;
  },
  
async getAllPayments(page?: number, size?: number, status?: string) {
  const response = await apiClient.get('/v1/payments/with-agency', {
    params: { page, size, status },
  });
  return response.data;
}
,

async getAllDemandByAgency(page?: number, size?: number) {
  const response = await apiClient.get('/v1/payments/by-agency', {
    params: { page, size},
  });
  return response.data;
}
,
  
  /**
   * Récupère les paiements manuels d'une agence spécifique
   * @param agencyId ID de l'agence
   * @param filters Filtres supplémentaires
   */
  async getAgencyPayments(agencyId: number, filters?: Omit<ManualPaymentFilter, 'agencyId'>) {
    const response = await apiClient.get(`/payments/manual/agency/${agencyId}`, {
      params: filters
    });
    
    return response.data;
  },
  
  /**
   * Obtient les détails d'un paiement manuel
   * @param paymentId ID du paiement
   */
  async getPaymentDetails(paymentId: number) {
    const response = await apiClient.get(`/v1/payments/${paymentId}`);
    return response.data;
  },
  
  /**
   * Approuve un paiement manuel (admin seulement)
   * @param paymentId ID du paiement
   * @param notes Notes administratives optionnelles
   */
  async approvePayment(paymentId: number, notes?: string) {
    const response = await apiClient.post(`/v1/balance/approve/${paymentId}`, { notes });
    return response.data;
  },
  
  // /**
  //  * Rejette un paiement manuel (admin seulement)
  //  * @param paymentId ID du paiement
  //  * @param reason Raison du rejet
  //  */
  // async rejectPayment(paymentId: number, reason: string) {
  //   const response = await apiClient.post(`/v1/balance/reject/${paymentId}`, { reason });
  //   return response.data;
  // },
  

  async rejectPayment(paymentId: number, reason: string) {
  const response = await apiClient.post(
    `/v1/balance/reject/${paymentId}`,
    null,
    {
      params: { reason }
    }
  );
  return response.data;
},

  /**
   * Associe un paiement manuel à une commande
   * @param paymentId ID du paiement
   * @param orderId ID de la commande
   */
  async linkPaymentToOrder(paymentId: number, orderId: number) {
    const response = await apiClient.post(`/payments/manual/${paymentId}/link-order/${orderId}`);
    return response.data;
  },
  
  /**
   * Télécharge la pièce jointe d'un paiement
   * @param paymentId ID du paiement
   */
  async downloadAttachment(paymentId: number) {
    const response = await apiClient.get(`/payments/manual/${paymentId}/attachment`, {
      responseType: 'blob'
    });
    
    return response.data;
  },
  
  /**
   * Génère un rapport des paiements manuels (admin seulement)
   * @param filters Filtres pour le rapport
   */
  async generateReport(filters?: ManualPaymentFilter) {
    const response = await apiClient.get('/payments/manual/report', {
      params: filters,
      responseType: 'blob'
    });
    
    return response.data;
  },
  
  /**
   * Met à jour le statut de traitement d'un paiement manuel
   * @param paymentId ID du paiement
   * @param status Nouveau statut
   * @param notes Notes administratives
   */
  async updateProcessingStatus(
    paymentId: number,
    status: 'PROCESSING' | 'COMPLETED' | 'ISSUE',
    notes?: string
  ) {
    const response = await apiClient.put(`/payments/manual/${paymentId}/processing-status`, {
      status,
      notes
    });
    
    return response.data;
  }
};
